import { BPRegMainParams } from 'legacy/lut/classes/models/onboarding/IRetailRequestData'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import OnboardingStoreService from 'legacy/lut/classes/services/OnboardingStoreService'
import { formRegConfig } from 'legacy/lut/components/widgets/public/onboarding/helpers/formRegConfig'
import { HELPER_STEP_NAMES } from 'legacy/lut/components/widgets/public/onboarding/models/stepNames'
import { lookupGetStateOld } from 'legacy/lut/store'

import { ICountry } from 'core/classes/models/IReference'
import Logger from 'core/utils/Logger'
import { readOr } from 'onboarding/api/legacyRidApi'
import { toRegistrationLanguageCode } from 'onboarding/helpers/registrationLanguage'
import { contactService } from 'onboarding/services/contactService'
import { requestService } from 'onboarding/services/requestService'
import { showErrorOnCatch } from 'onboarding/utils/errorResponse/showErrorOnCatch'

export type TBPRegistrationRequest = {
  requestId: number
  requestUid?: string
}

const fetchContactLanguage = async (contactId?: string): Promise<string> => {
  if (!contactId) {
    return ''
  }

  try {
    const contact = await contactService.fetchByContactId(contactId, 'bp-registration-language')

    return toRegistrationLanguageCode(contact?.PreferredLanguage)
  } catch (e) {
    Logger.error(`BP registration: preferred language of contact ${contactId} was not read`, e)

    return ''
  }
}

const createPfpData = async (
  requestId: number,
  email: string,
  partnerCountry: string,
  contactId?: string
) => {
  const country =
    lookupGetStateOld().references.countries.find((item) => item.name === partnerCountry) ||
    ({} as ICountry)

  const language = await fetchContactLanguage(contactId)

  const initialData = {
    Email: email,
    MainCountry: country.value || '',
    DisableEmail: 1,
    ...(language ? { Language: language } : {}),
  }

  const res = await OnboardingStoreService.getFormConfig(requestId, 'BPRegistration-form')

  // Runs immediately after this caller created the request, so a refusal is not a case here: it
  // would mean the creator cannot read what it just made. An outage is, though — and then the
  // request is created but never started, with nothing else to say so.
  if (res.outcome !== 'ok') {
    Logger.error(
      `[PFP_LEGACY_RID] BP registration ${requestId} was created but its step map could not be read (${res.outcome}); the contact check was not started`
    )
  }

  const startContactCheckAction = formRegConfig(readOr(res, []))[
    HELPER_STEP_NAMES.CheckContactPartnerId
  ]
  if (startContactCheckAction) {
    const { actionId, transitionId, requestId } = startContactCheckAction
    await OnboardingStoreService.submitBPStep({
      actionId,
      transitionId,
      requestId,
      onboardingRequestData: initialData,
      modifiedFields: language ? ['Email', 'MainCountry', 'Language'] : ['Email', 'MainCountry'],
    })
  }
}

export const createRegWithInitialData = async (
  mainParam: string,
  partnerId: string,
  requestor: string,
  country: string,
  contactEmail?: string,
  contactId?: string
) => {
  const initialCollectedData = {}
  if (contactEmail) {
    initialCollectedData['bpEmail'] = contactEmail.toLowerCase()
  }
  if (contactId) {
    initialCollectedData['bpContactId'] = contactId
  }
  const reqProps = { MainParameter: mainParam }
  let request: TBPRegistrationRequest = { requestId: 0 }
  try {
    request = await requestService.createAndStartOneWithUid({
      RequestProps: reqProps,
      Type: RequestTypes.BPRegistration,
      params: {
        PartnerId: partnerId,
        Requestor: requestor,
      },
      initialCollectedData,
    })
    const { requestId } = request
    if (requestId && contactEmail && mainParam !== BPRegMainParams.SelfRegistr) {
      await createPfpData(requestId, contactEmail, country, contactId)
    }
  } catch (e: any) {
    showErrorOnCatch(e)
  }

  return request
}
